import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  isPartnerClalit,
  isPartnerMaoflaamit,
  isPartnerOvdimBari,
  selectIsOrthodox,
  selectPartner,
} from "../../../store/partnerStore";
import {
  checkCanPurchasePass,
  selectCanPurchaseChecked,
  selectCanPurchaseLoading,
} from "../../../store/multipleEntryPassSlice";
import { selectClientAddress } from "../../../store/userSlice";
import { getPartnerAsset } from "../../theme/partnerAssets";
import { formatAddress } from "../../utils/address";
import useEntryGpsRefresh from "../../hooks/useEntryGpsRefresh";
import usePassState, { PASS_STATE } from "../../hooks/usePassState";
import useUserLocation from "../../hooks/useUserLocation";
import fetchAppParams from "../../utils/fetchAppParams";
import useHomeCarousels from "./useHomeCarousels";
import useHomeLoader from "./useHomeLoader";

// Data layer for the V2 home page. Gated from screens/mainPage.jsx, which has
// no reusable data of its own, so everything lives here per the v2 README.
//
// Every fetch below is deliberately swalDialog-free: a home page must never
// throw a modal over itself because a background list failed.
export default function useHomeData() {
  const dispatch = useDispatch();
  const partner = useSelector(selectPartner);
  const isOrthodox = useSelector(selectIsOrthodox);
  const clientAddress = useSelector(selectClientAddress);
  const userParams = useSelector((state) => state.userSlice.userData);
  const token = useSelector((state) => state.userSlice.mainUserData?.Token);
  const id = useSelector((state) => state.userSlice.mainUserData?.ID);
  const phone = useSelector((state) => state.userSlice.mainUserData?.Phone);
  const allAppParams = useSelector((state) => state.appParamSlice.allAppParams);

  const binID = userParams?.BinID;
  const appParamsLoaded = Object.keys(allAppParams).length > 0;

  const isClalit = useSelector(isPartnerClalit);
  const isOvdimBari = useSelector(isPartnerOvdimBari);
  const isMaoflaamit = useSelector(isPartnerMaoflaamit);
  const canPurchaseChecked = useSelector(selectCanPurchaseChecked);
  const canPurchaseLoading = useSelector(selectCanPurchaseLoading);

  const { coords, source } = useUserLocation();
  // Location on entry, now that welcome runs only on the first onboarding:
  // refreshes a granted fix silently, and raises the permission prompt (once per
  // page load) while the user has never answered it. Its settled signal is what
  // lets the carousel gate below fetch once, with the final location.
  const gpsSettled = useEntryGpsRefresh();
  const passState = usePassState();

  // The purchase-eligibility check, fired the moment credentials land — mainPage's
  // effect runs after all of these (parent effects run last) so without this the
  // GetCarousels call would hit the wire first, and the loader gate waits on this
  // answer. Same partner condition as mainPage.jsx; its later duplicate dispatch
  // is absorbed by the thunk's in-flight condition. Declared before
  // useHomeCarousels so this effect — and its request — runs first.
  useEffect(() => {
    if (canPurchaseChecked || canPurchaseLoading) return;
    if (!(isClalit || isOvdimBari || isMaoflaamit)) return;
    if (!token || !id || !phone) return;
    dispatch(checkCanPurchasePass({ token, id, phone }));
  }, [token, id, phone, canPurchaseChecked, canPurchaseLoading, isClalit, isOvdimBari, isMaoflaamit, dispatch]);

  const { rows, allFailed, retry } = useHomeCarousels({ coords, source, gpsSettled });
  // Full-screen overlay for a cold load only — warm (prefetched) mounts never show it.
  useHomeLoader(rows);

  // GetCarousels returns no price, so the card price comes from the same
  // ClubList that the search and venue screens use — three screens quoting
  // three prices is a bug waiting to happen. Both of those screens already
  // fire this call on entry, so it's paid earlier here, not extra.
  useEffect(() => {
    if (appParamsLoaded) return;
    if (!binID || !token || !id || !phone) return;
    fetchAppParams({ binID, token, id, phone, dispatch });
  }, [binID, token, id, phone, appParamsLoaded, dispatch]);

  const priceForClub = useCallback(
    (clubId) => {
      const club = allAppParams?.ClubList?.find((el) => el.RecordID === Number(clubId));
      return club?.Price > 0 ? club.Price : null;
    },
    [allAppParams],
  );

  return {
    partner,
    // The address picked by hand, else the one on the user's profile from the Login
    // response — the same two sources, in the same order, that useUserLocation reads
    // coordinates from. Reading only the first is why the pill used to show the
    // prompt while the carousels were already ranking by distance.
    // null → the top bar shows the "choose a location" prompt instead.
    locationLabel:
      clientAddress?.formatted || formatAddress(userParams?.Street, userParams?.HouseNumber, userParams?.City) || null,
    passState,
    // An active pass covers every visit, so a price tag would mislead; while the
    // pass list is still loading ("hidden") prices stay off too, so a pass holder
    // never sees them flash on first paint.
    showPrices: passState.state === PASS_STATE.NONE || passState.state === PASS_STATE.INACTIVE,
    rows,
    allFailed,
    retry,
    priceForClub,
    passArt: getPartnerAsset(partner, "passCardImg"),
    // Orthodox users get the neutral partner image instead of the club's own,
    // matching the rule in fitExtendSelector.
    defaultClubImg: isOrthodox ? getPartnerAsset(partner, "defaultClubImg") : null,
    imgFallbackSrc: getPartnerAsset(partner, "clubFallbackImg"),
  };
}
